import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchBrandProfiles as apiFetchBrandProfiles,
  fetchQuota as apiFetchQuota,
  startBrandProfileGeneration,
  pollBrandProfileGeneration,
  startPageGeneration as apiStartPageGeneration,
  pollPageGeneration,
  type BrandProfileResponse,
  type QuotaResponse,
} from '@/services/aiPageGenerationApi';

const BRAND_PROFILE_POLL_INTERVAL = 3000;
const BRAND_PROFILE_MAX_POLLS = 20; // 60s timeout

const PAGE_GENERATION_POLL_INTERVAL = 3000;
const PAGE_GENERATION_MAX_POLLS = 60; // 3min timeout

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const loadQuota = createAsyncThunk<QuotaResponse>('wizard/loadQuota', async () => {
  return await apiFetchQuota();
});

export const loadBrandProfiles = createAsyncThunk<BrandProfileResponse[]>(
  'wizard/loadBrandProfiles',
  async () => {
    return await apiFetchBrandProfiles();
  },
);

export const generateBrandProfile = createAsyncThunk<
  BrandProfileResponse,
  string,
  { rejectValue: string }
>('wizard/generateBrandProfile', async (websiteUrl, { rejectWithValue }) => {
  const { id: sessionId } = await startBrandProfileGeneration(websiteUrl);

  for (let i = 0; i < BRAND_PROFILE_MAX_POLLS; i++) {
    await sleep(BRAND_PROFILE_POLL_INTERVAL);

    const result = await pollBrandProfileGeneration(sessionId);

    if (result.status === 'ready') {
      return result.brandProfile;
    }

    if (result.status === 'failed') {
      return rejectWithValue(result.error ?? 'Brand profile generation failed');
    }
  }

  return rejectWithValue('Brand profile generation timed out');
});

export const generatePage = createAsyncThunk<
  string,
  {
    brandProfileId: string | null;
    globalStyleId: string | null;
    goal: string;
    description: string;
  },
  { rejectValue: string }
>('wizard/generatePage', async (params, { rejectWithValue }) => {
  let jobId: string;
  try {
    const result = await apiStartPageGeneration(params);
    jobId = result.id;
  } catch (error) {
    if (
      error instanceof XMLHttpRequest &&
      error.status === 400 &&
      error.responseText?.includes('Monthly AI page generation limit reached')
    ) {
      return rejectWithValue('QUOTA_EXCEEDED');
    }
    throw error;
  }

  for (let i = 0; i < PAGE_GENERATION_MAX_POLLS; i++) {
    await sleep(PAGE_GENERATION_POLL_INTERVAL);

    const result = await pollPageGeneration(jobId);

    if (result.status === 'completed') {
      return result.pageId;
    }

    if (result.status === 'failed') {
      return rejectWithValue(result.error ?? 'Page generation failed, please try again.');
    }
  }

  return rejectWithValue('Page generation timed out');
});
